import { LegalLayout, Section } from "@/pages/legal/LegalLayout";
import { COMPANY } from "@/lib/site-data";

const Refunds = () => {
  return (
    <LegalLayout
      testid="page-refunds"
      title="Refund Policy"
      updated="December 2025"
      lead="A short, transparent explanation of when — and how — fees paid to Dream Technology sp. z o.o. may be adjusted, refunded or credited back to a Client."
    >
      <Section number="01" title="Nature of our services">
        <p>
          {COMPANY.legalName} is a business-to-business professional services
          firm. We do not sell consumer goods, subscriptions or downloadable
          products through {COMPANY.domain}. All commercial services are
          delivered under a written contract (an “<strong>Engagement</strong>”),
          typically a Master Services Agreement and one or more Statements of
          Work (SOWs) signed by both parties.
        </p>
        <p>
          Because our engagements involve senior expert time allocated in
          advance to a specific Client, the refund arrangements set out below
          apply in place of any generic e-commerce refund regime.
        </p>
      </Section>

      <Section number="02" title="Discovery & assessment phases">
        <p>
          Where an Engagement includes a fixed-fee discovery, diagnostic or
          feasibility phase, the corresponding fee becomes payable in full on
          the delivery of the written output (opinion, report, roadmap). Once
          the output has been delivered, the fee is non-refundable.
        </p>
        <p>
          If, however, we materially fail to deliver the agreed output within
          the timeline set in the SOW and cannot remedy that failure within
          fifteen (15) business days of a written notice, the Client is
          entitled to a full refund of the relevant fee.
        </p>
      </Section>

      <Section number="03" title="Time-and-materials engagements">
        <p>
          For engagements charged on a time-and-materials basis, invoices are
          issued monthly in arrears against timesheets. Fees for work already
          performed and accepted by the Client are not refundable. Any
          disputed portion of an invoice must be flagged in writing within
          fifteen (15) days of receipt and will be resolved in accordance with
          the dispute-resolution clause of the applicable MSA.
        </p>
      </Section>

      <Section number="04" title="Fixed-fee & milestone engagements">
        <p>
          Where the SOW specifies fixed fees payable against defined
          milestones, each milestone payment becomes non-refundable once the
          corresponding milestone has been accepted by the Client. If the
          Client terminates an Engagement for convenience before a milestone
          is reached, we will invoice for work performed on a pro-rata basis
          up to the effective date of termination, and refund any advance
          payment that exceeds that amount within thirty (30) days.
        </p>
      </Section>

      <Section number="05" title="Retainers">
        <p>
          Retainers reserve a defined capacity of our team for the Client’s
          benefit. Unused hours within a retainer month do not accrue and are
          not refundable, unless expressly stated otherwise in the SOW.
          Retainers may be terminated in accordance with the notice period
          set out in the applicable contract, typically thirty (30) days.
        </p>
      </Section>

      <Section number="06" title="Termination for cause">
        <p>
          Either party may terminate an Engagement with immediate effect if
          the other party materially breaches its obligations and fails to
          cure the breach within thirty (30) days of written notice. Where the
          Engagement is terminated for cause by the Client, we will refund
          any prepaid fees that relate to services not yet delivered.
        </p>
      </Section>

      <Section number="07" title="Refund process">
        <p>Approved refunds are processed as follows:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>refunds are issued by SEPA bank transfer to the account originally used by the Client for payment;</li>
          <li>we may issue a corrective VAT invoice (<em>faktura korygująca</em>) where required by Polish accounting law;</li>
          <li>refunds are typically settled within thirty (30) calendar days of the refund being agreed in writing.</li>
        </ul>
      </Section>

      <Section number="08" title="Disputes">
        <p>
          If you disagree with a fee, invoice or refund position, please
          contact us first at{" "}
          <a
            href={`mailto:${COMPANY.email}`}
            className="text-emerald-800 underline underline-offset-2"
          >
            {COMPANY.email}
          </a>{" "}
          — most matters are resolved informally within a few business days.
          Any unresolved dispute is subject to the governing-law and
          jurisdiction clause of the applicable contract (Polish law; courts
          competent for Warsaw).
        </p>
      </Section>

      <Section number="09" title="Consumer rights">
        <p>
          Where, exceptionally, a natural person acting outside of their
          business or professional activity engages with us as a consumer
          within the meaning of Art. 22¹ of the Polish Civil Code, statutory
          consumer rights — including the fourteen (14) day right of
          withdrawal from a distance contract under the Polish Act on Consumer
          Rights of 30 May 2014 — apply in addition to, and prevail over,
          anything less favourable in this policy.
        </p>
      </Section>
    </LegalLayout>
  );
};

export default Refunds;
